import { call, put, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { getCurrentToken } from '../../features/account/login/login.api';
import { SessionInfoDto } from '../../features/account/login/login.dtos';
import { initializeAuthActions, userLoggedIn } from '../actions/auth.actions';
import { AuthDto, authInit } from '../dtos/auth';
import {
  storageRoles,
  storageAuthInformation,
} from '../constants/strings';

/**
 * Initializes auth state by calling /current-token endpoint.
 * This reads the HttpOnly cookie and hydrates Redux state with session metadata.
 * Cookie-based auth only. No token storage in JS.
 */
function* initializeAuth() {
  try {
    const sessionInfo: SessionInfoDto = yield call(getCurrentToken);

    if (sessionInfo && sessionInfo.userId && sessionInfo.roles && sessionInfo.roles.length > 0) {
      // Build AuthDto with empty tokens (authentication via HttpOnly cookie)
      const auth: AuthDto = {
        status: 'authenticated',  // Auth hydration succeeded
        authToken: '',  // Empty - authentication via HttpOnly cookie only
        refreshToken: '',  // Empty - authentication via HttpOnly cookie only
        isAdmin: Boolean(sessionInfo.roles.find((r: string) => r === 'admin')),
        isAuthenticated: true,
        isPasswordExpired: sessionInfo.isPasswordExpired,
        roles: sessionInfo.roles,
        fullName: sessionInfo.fullName,
        employerId: '',  // Not in SessionInfoDto
        contactId: '',  // Not in SessionInfoDto
        employers: sessionInfo.employers.map(e => e.toString()),
      };

      // Store only non-sensitive session metadata (roles, user info)
      // Do NOT store AuthToken or RefreshToken
      sessionStorage.setItem(storageRoles, JSON.stringify(sessionInfo.roles));
      sessionStorage.setItem(storageAuthInformation, JSON.stringify({
        userId: sessionInfo.userId,
        userName: sessionInfo.userName,
        fullName: sessionInfo.fullName,
        roles: sessionInfo.roles,
        employers: sessionInfo.employers,
        tenant: sessionInfo.tenant,
        isPasswordExpired: sessionInfo.isPasswordExpired,
        // Explicitly exclude authToken and refreshToken
      }));

      yield put({ type: getType(userLoggedIn), payload: auth });
      yield put({ type: getType(initializeAuthActions.success), payload: sessionInfo });
    } else {
      // No valid session, clear auth state
      const anonymousAuth: AuthDto = {
        ...authInit,
        status: 'anonymous',  // Auth hydration failed - user is anonymous
      };
      yield put({ type: getType(userLoggedIn), payload: anonymousAuth });
      yield put({ type: getType(initializeAuthActions.failure), payload: 'No valid session' });
    }
  } catch (error) {
    // 401 or other error - clear auth state and sessionStorage metadata
    sessionStorage.removeItem(storageRoles);
    sessionStorage.removeItem(storageAuthInformation);
    const anonymousAuth: AuthDto = {
      ...authInit,
      status: 'anonymous',  // Auth hydration failed - user is anonymous
    };
    yield put({ type: getType(userLoggedIn), payload: anonymousAuth });
    yield put({ type: getType(initializeAuthActions.failure), payload: 'Unauthorized' });
  }
}

export function* watchInitializeAuth() {
  yield takeLatest(getType(initializeAuthActions.request), initializeAuth);
}
